import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, Link } from 'react-router-dom';
import { LogIn, Eye, EyeOff, AlertCircle, ArrowRight } from 'lucide-react';
import { useAuth } from '../lib/auth';
import '../styles/LoginPage.css';

// Schéma de validation
const loginSchema = z.object({
  email: z.string().email('Email invalide'),
  password: z.string().min(1, 'Le mot de passe est requis'),
  rememberMe: z.boolean().optional(),
});

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(loginSchema),
  });

  // CORRECTION ICI : On récupère l'événement 'e' en deuxième argument
  const onSubmit = async (data, e) => {
    // 🛑 BLINDAGE : On empêche le rechargement de force
    if (e) e.preventDefault();

    setFormError(null);
    setIsLoading(true);

    try {
      await login(data.email, data.password, data.rememberMe);
      // Si tout se passe bien, on redirige
      navigate('/');
    } catch (err) {
      console.error("Erreur Login:", err);
      // On affiche l'erreur sans recharger la page
      setFormError(
        err.message?.includes("401") 
          ? "Email ou mot de passe incorrect." 
          : "Une erreur est survenue. Vérifiez vos identifiants."
      );
    } finally {
      // On arrête le chargement quoi qu'il arrive
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        
        {/* En-tête */}
        <div className="login-header">
          <div className="login-icon-wrapper">
             <LogIn size={32} />
          </div>
          <h2>Bon retour parmi nous !</h2>
          <p className="login-subtitle">Connectez-vous pour accéder à votre espace NMS 06.</p>
        </div>

        {/* Zone d'erreur (reste affichée maintenant) */}
        {formError && (
          <div className="error-banner">
            <AlertCircle size={20} />
            <span>{formError}</span>
          </div>
        )}

        {/* Formulaire */}
        <form className="login-form" onSubmit={handleSubmit(onSubmit)}>
          
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              {...register('email')}
              type="email"
              id="email"
              placeholder="votre@email.com"
              className={errors.email ? 'input-error' : ''}
              autoComplete="username"
              disabled={isLoading}
            />
            {errors.email && <span className="error-text">{errors.email.message}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="password">Mot de passe</label>
            <div className="password-wrapper">
              <input
                {...register('password')}
                type={showPassword ? 'text' : 'password'}
                id="password"
                placeholder="Votre mot de passe"
                className={errors.password ? 'input-error' : ''}
                autoComplete="current-password"
                disabled={isLoading}
              />
              <button
                type="button"
                className="toggle-password-btn"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex="-1"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.password && <span className="error-text">{errors.password.message}</span>}
          </div>

          <div className="form-options">
            <label className="remember-me">
              <input {...register('rememberMe')} type="checkbox" />
              <span>Se souvenir de moi</span>
            </label>
            {/* CORRECTION : className au lieu de class */}
            <Link to="/forgot-password" className="forgot-link">Mot de passe oublié ?</Link>
          </div>

          <button type="submit" className="btn-login" disabled={isLoading}>
            {isLoading ? "Connexion..." : "Se connecter"} <ArrowRight size={18} />
          </button>

        </form>

        {/* Pied de carte */}
        <div className="login-footer">
          <p>Pas encore de compte ? <Link to="/register">Créer un compte</Link></p>
        </div>
      </div>
    </div>
  );
}